/**
 * Privacy Act SOA Service
 * 
 * Service for managing Privacy Act Statement of Applicability (SOA)
 * including fetching controls, tasks, and saving SOA responses.
 * 
 * Created: 2025-11-28
 * Updated: 2025-12-17 - Fully migrated to Thalassa API
 */

import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

// Types
export interface PAControl {
  controlID: string;
  controlName: string;
  controlDescription: string;
  deitID: string;
  implementationGuidance: string;
  paSuggestedAssuranceID: string | string[];
}

export interface PATask {
  assID: string;
  checkDetail: {
    active: string;
    assignedTo: string;
    check: string;
    evidence: string;
    framework: Record<string, { frameName: string }>;
    paDetails?: {
      guidanceNotes: string;
      paCategory: string[];
      paSuggestedEvidence: string;
    };
    procRef?: string[];
  };
  checkFrequency: {
    checkDays: string;
    lastCheck?: Record<string, { checkDate: string; checkStatus: string }>;
    nextCheck: string;
    reminderActive: string;
    reminderDays: string;
    sendReminder: string;
  };
  checkHistory?: Record<string, any>;
  controlID?: string[];
}

export interface SOAAnswer {
  questionId: string;
  controlId: string;
  answer: 'yes' | 'no' | 'unsure';
}

export interface PAControlSOAEntry {
  controlID: string;
  controlName: string;
  controlDesc: string;
  controlApplicable: boolean;
  controlJustification: string;
  implementationStatus: 'Fully implemented' | 'Not implemented' | 'Not applicable';
  framework: string;
  owner: string;
  updatedAt: any;
  updatedBy: string;
}

/**
 * Maps question IDs to control IDs
 */
export const QUESTION_TO_CONTROL_MAP: Record<string, string> = {
  'app-1': 'PA.APP01',
  'app-2': 'PA.APP02',
  'app-3': 'PA.APP03',
  'app-4': 'PA.APP04',
  'app-5': 'PA.APP05',
  'app-6': 'PA.APP06',
  'app-7': 'PA.APP07',
  'app-8': 'PA.APP08',
  'app-9': 'PA.APP09',
  'app-10': 'PA.APP10',
  'app-11': 'PA.APP11',
  'app-12': 'PA.APP12',
  'app-13': 'PA.APP13',
};

/**
 * Fetches all Privacy Act controls from Thalassa API
 */
export async function fetchPAControls(): Promise<Record<string, PAControl>> {
  console.log('[PrivacyActSOAService] Fetching PA controls via Thalassa API');
  try {
    const res = await deiterateAPI.GET('/v1/soa/pa/controls', {
      mode: 'cors',
      backend: 'thalassa'
    });
    return res.controls || {};
  } catch (error) {
    console.error('[PrivacyActSOAService] Error fetching PA controls:', error);
    return {};
  }
}

// Helper to get headers
function getHeaders(companyId: string) {
  return {
    'X-Tenancy-Id': companyId,
    'Company-ID': companyId,
  };
}

/**
 * Fetches all Privacy Act assurance tasks via Thalassa API
 */
export async function fetchPATasks(): Promise<Record<string, PATask>> {
  console.log('[PrivacyActSOAService] Fetching PA tasks via Thalassa API');
  try {
    const res = await deiterateAPI.GET('/v1/soa/pa/tasks', {
      mode: 'cors',
      backend: 'thalassa'
    });
    return res.tasks || {};
  } catch (error) {
    console.error('[PrivacyActSOAService] Error fetching PA tasks:', error);
    return {};
  }
}

/**
 * Gets the suggested assurance task IDs for a control
 */
export function getSuggestedTaskIds(control: PAControl): string[] {
  if (!control.paSuggestedAssuranceID) {
    return [];
  }
  
  if (Array.isArray(control.paSuggestedAssuranceID)) {
    return control.paSuggestedAssuranceID;
  }
  
  // Handle single string value
  return [control.paSuggestedAssuranceID];
}

/**
 * Saves the Privacy Act SOA entries via Thalassa API
 * Returns counts of applicable and not applicable controls
 */
export async function savePrivacyActSOA(
  companyId: string,
  answers: SOAAnswer[],
  controls: Record<string, PAControl>
): Promise<{ applicable: number; notApplicable: number }> {
  try {
    console.log('[PrivacyActSOAService] Saving Privacy Act SOA via Thalassa API');
    
    const res = await deiterateAPI.POST('/v1/soa/pa/save', {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(companyId),
      body: JSON.stringify({ answers, controls }),
    });

    if (res.statusCode !== 200) {
      throw new Error(res.error || 'Failed to save Privacy Act SOA');
    }

    console.log('[PrivacyActSOAService] Saved SOA entries - applicable:', res.applicable, 'not applicable:', res.notApplicable);
    return { applicable: res.applicable || 0, notApplicable: res.notApplicable || 0 };
  } catch (error) {
    console.error('[PrivacyActSOAService] Error saving Privacy Act SOA:', error);
    throw error;
  }
}

/**
 * Creates assurance tasks for applicable controls via Thalassa API
 */
export async function createPrivacyActAssuranceTasks(
  companyId: string,
  answers: SOAAnswer[],
  controls: Record<string, PAControl>,
  globalTasks: Record<string, PATask>
): Promise<{ created: number; skipped: number }> {
  try {
    console.log('[PrivacyActSOAService] Creating assurance tasks via Thalassa API');
    
    const res = await deiterateAPI.POST('/v1/soa/pa/create-tasks', {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(companyId),
      body: JSON.stringify({ answers, controls, globalTasks }),
    });

    if (res.statusCode !== 200) {
      throw new Error(res.error || 'Failed to create Privacy Act tasks');
    }

    console.log('[PrivacyActSOAService] Created', res.created, 'assurance tasks, skipped', res.skipped);
    return { created: res.created || 0, skipped: res.skipped || 0 };
  } catch (error) {
    console.error('[PrivacyActSOAService] Error creating assurance tasks:', error);
    throw error;
  }
}

/**
 * Complete Privacy Act SOA builder flow
 * Saves SOA entries and creates assurance tasks
 */
export async function completePrivacyActSOA(
  companyId: string,
  answers: SOAAnswer[]
): Promise<{ soaSaved: boolean; controlsApplicable: number; controlsNotApplicable: number; tasksCreated: number; tasksSkipped: number }> {
  try {
    console.log('[PrivacyActSOAService] Starting complete Privacy Act SOA flow');
    
    // Fetch controls and tasks in parallel
    const [controls, tasks] = await Promise.all([
      fetchPAControls(),
      fetchPATasks()
    ]);
    
    // Save SOA entries
    const { applicable, notApplicable } = await savePrivacyActSOA(companyId, answers, controls);
    
    // Create assurance tasks
    const { created, skipped } = await createPrivacyActAssuranceTasks(
      companyId,
      answers,
      controls,
      tasks
    );
    
    console.log('[PrivacyActSOAService] Complete! Applicable:', applicable, 'Not applicable:', notApplicable, 'Tasks created:', created, 'skipped:', skipped);
    
    return {
      soaSaved: true,
      controlsApplicable: applicable,
      controlsNotApplicable: notApplicable,
      tasksCreated: created,
      tasksSkipped: skipped
    };
  } catch (error) {
    console.error('[PrivacyActSOAService] Error in complete flow:', error);
    throw error;
  }
}

export default {
  fetchPAControls,
  fetchPATasks,
  getSuggestedTaskIds,
  savePrivacyActSOA,
  createPrivacyActAssuranceTasks,
  completePrivacyActSOA,
  QUESTION_TO_CONTROL_MAP,
};
